import {
  CREDIT_NOTE,
  TRANSACTION_TYPE_BANK_TRANSFER,
  TRANSACTION_TYPE_OTHER,
  TRANSACTION_TYPE_CREDIT_CARD,
  TRANSACTION_TYPE_INTERNET,
} from '../../config/constants';

const EPSILON = 0.01;

const PINPAD_TRANSACTION_CODES = [
  TRANSACTION_TYPE_CREDIT_CARD,
  TRANSACTION_TYPE_INTERNET,
];

const PINPAD_CARD_FIELDS = [
  'operationCode',
  'businessName',
  'cardholderName',
  'affiliationNumber',
  'address',
  'signature',
  'cardApplicationId',
  'cardCryptogram',
  'terminalSerial',
  'installments',
  'installmentAmount',
];

export const extractCreditNotes = (creditNotes) =>
  Object.values(creditNotes || {}).filter(
    (n) => n && typeof n === 'object' && 'value' in n,
  );

const rowCode = (row) =>
  row && row.paymentMethod ? row.paymentMethod.transactionCode : null;

const rowAmount = (row) => parseFloat(row && row.amount) || 0;

export const summarizeSplit = (transactionList = [], totalPrice = 0) => {
  const assigned = transactionList.reduce((sum, r) => sum + rowAmount(r), 0);

  const creditAssigned = transactionList.reduce((sum, r) => {
    const allowsOverpay =
      rowCode(r) === CREDIT_NOTE &&
      extractCreditNotes(r.creditNotes).length > 0;
    return allowsOverpay ? sum + rowAmount(r) : sum;
  }, 0);

  const regularAssigned = assigned - creditAssigned;
  const remaining = Math.round((totalPrice - assigned) * 100) / 100;

  const hasRegularMethod = transactionList.some(
    (r) => r.paymentMethod && rowCode(r) !== CREDIT_NOTE,
  );

  const isPending = remaining > EPSILON / 2;
  const isExceeded =
    !isPending &&
    remaining < -EPSILON / 2 &&
    (hasRegularMethod || regularAssigned - totalPrice > EPSILON / 2);
  const isOverpaidByCredit =
    !isPending && !isExceeded && remaining < -EPSILON / 2;

  const methodValues = transactionList
    .map((r) => (r.paymentMethod ? r.paymentMethod.value : null))
    .filter((value) => value !== null && value !== undefined);

  const hasDuplicateMethods =
    new Set(methodValues).size !== methodValues.length;

  return {
    assigned,
    regularAssigned,
    remaining,
    isPending,
    isExceeded,
    isOverpaidByCredit,
    hasDuplicateMethods,
    isValid: !isPending && !isExceeded && !hasDuplicateMethods,
  };
};

export const buildTransactionList = (
  transactionList = [],
  { totalPrice, customerId, voucherTypeId, businessId },
) => {
  const transactions = [];
  const freeOfCharge = {
    categoryId: null,
    subCategoryId: null,
    userId: null,
  };

  const orderedRows = [...transactionList].sort((a, b) => {
    const aIsCredit = rowCode(a) === CREDIT_NOTE ? 0 : 1;
    const bIsCredit = rowCode(b) === CREDIT_NOTE ? 0 : 1;
    return aIsCredit - bIsCredit;
  });

  const nonCreditTotal = transactionList.reduce(
    (sum, r) => (rowCode(r) !== CREDIT_NOTE ? sum + rowAmount(r) : sum),
    0,
  );
  let creditTarget = Math.max(totalPrice - nonCreditTotal, 0);

  orderedRows.forEach((row) => {
    const code = rowCode(row);
    const base = {
      customerId,
      paymentMethodId: row.paymentMethod ? row.paymentMethod.value : null,
      voucherTypeId,
      voucherCode: row.voucherCode || null,
      businessId,
      amount: rowAmount(row),
    };

    if (code === CREDIT_NOTE) {
      extractCreditNotes(row.creditNotes)
        .slice()
        .sort(
          (a, b) => (parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0),
        )
        .forEach((note) => {
          if (creditTarget <= 0) return;
          const applied = Math.min(parseFloat(note.amount) || 0, creditTarget);
          creditTarget -= applied;
          transactions.push({
            ...base,
            creditNoteId: note.value,
            amount: applied,
          });
        });
      return;
    }

    if (code === TRANSACTION_TYPE_BANK_TRANSFER) {
      base.bankAccountId = row.bankAccount ? row.bankAccount.value : null;
      base.depositDate = row.depositDate || null;
    }

    if (PINPAD_TRANSACTION_CODES.includes(code) && row.pinpadResponse) {
      PINPAD_CARD_FIELDS.forEach((field) => {
        const value = row.pinpadResponse[field];
        base[field] = value === undefined ? null : value;
      });
    }

    if (code === TRANSACTION_TYPE_OTHER) {
      freeOfCharge.categoryId = row.freeOfChargeCategoryId
        ? row.freeOfChargeCategoryId.value
        : null;
      freeOfCharge.subCategoryId = row.freeOfChargeSubCategory
        ? row.freeOfChargeSubCategory.value
        : null;
      freeOfCharge.userId = row.freeOfChargeUser
        ? row.freeOfChargeUser.value
        : null;
    }

    transactions.push(base);
  });

  return { transactions, freeOfCharge };
};

export const getRowTransactionCode = (rowValues) =>
  rowValues && rowValues.paymentMethod
    ? rowValues.paymentMethod.transactionCode
    : null;

export const flagUsedPinpadTransactions = (
  pinPadTransactions = [],
  transactionList = [],
) => {
  const activeReferences = new Set(
    transactionList
      .filter((row) => row.pinpadPaid && row.voucherCode)
      .map((row) => row.voucherCode),
  );

  return pinPadTransactions.map((transaction) => ({
    ...transaction,
    used:
      transaction.operation === 'SALE' &&
      Boolean(transaction.approved) &&
      activeReferences.has(transaction.financialReference),
  }));
};
